// ============================================================================
// BeoflowIndicator — the canonical value-direction / status glyph.
// The iOS custom indicator SVGs (Assets.xcassets), inlined so they tint via
// currentColor. Spec: src/theme/BeoflowIndicatorSpec.generated.ts
//   directions: upArrow · upIndicator · neutral · downIndicator · downArrow · alert
//   styles:     positive = meanBlue · important = peakOrange · normal = beoWhite
// ============================================================================

import { Box } from '@mui/material'
import { indicatorSpec } from '@/theme/BeoflowIndicatorSpec.generated'

/** Filled-glyph SVG paths (viewBox 0 0 72 72), tinted via fill=currentColor. */
const INDICATOR_PATHS = {
  upArrow: 'M4 30.6667C18.7276 30.6667 30.6667 18.7276 30.6667 4H41.3333C41.3333 18.7276 53.2724 30.6667 68 30.6667V41.3333C57.5537 41.3333 48.1094 37.0428 41.3333 30.1279L41.3333 68H30.6667L30.6667 30.1279C23.8906 37.0428 14.4463 41.3333 4 41.3333V30.6667Z',
  upIndicator: 'M30.3333 15C30.3333 31.5685 17.6481 45 2 45L2 57C16.4292 57 29.0589 48.8425 36 36.6455C42.9411 48.8425 55.5708 57 70 57L70 45C54.3519 45 41.6667 31.5685 41.6667 15L30.3333 15Z',
  neutral: 'M31.2026 33.0928C42.0992 21.5105 60.3642 20.9143 71.9985 31.7559L63.7759 40.4961C57.2663 34.4301 47.2032 34.5039 40.7856 40.4746L40.7964 40.4844C29.8999 52.0667 11.6348 52.6629 0.000488281 41.8213L8.22314 33.0811C14.7328 39.147 24.7949 39.0724 31.2124 33.1016L31.2026 33.0928Z',
  downIndicator: 'M30.3333 57C30.3333 40.4315 17.6481 27 2 27L2 15C16.4292 15 29.0589 23.1575 36 35.3545C42.9411 23.1575 55.5708 15 70 15L70 27C54.3519 27 41.6667 40.4315 41.6667 57L30.3333 57Z',
  downArrow: 'M4 41.3333C18.7276 41.3333 30.6667 53.2724 30.6667 68H41.3333C41.3333 53.2724 53.2724 41.3333 68 41.3333V30.6667C57.5537 30.6667 48.1094 34.9572 41.3333 41.8721L41.3333 4H30.6667L30.6667 41.8721C23.8906 34.9572 14.4463 30.6667 4 30.6667V41.3333Z',
  // The "!" alert status mark — square bar + square dot, drawn in the indicator's filled-glyph
  // geometry (squared terminals, ties to the 4x4 square-dot motif). Mirrors iOS alert-indicator-icon.
  alert: 'M31.5 8H40.5A3.5 3.5 0 0 1 44 11.5V41.5A3.5 3.5 0 0 1 40.5 45H31.5A3.5 3.5 0 0 1 28 41.5V11.5A3.5 3.5 0 0 1 31.5 8ZM31.5 52H40.5A3.5 3.5 0 0 1 44 55.5V64.5A3.5 3.5 0 0 1 40.5 68H31.5A3.5 3.5 0 0 1 28 64.5V55.5A3.5 3.5 0 0 1 31.5 52Z',
} as const

export type IndicatorDirection = keyof typeof INDICATOR_PATHS

type IndicatorAccessibility =
  | { decorative: true; accessibilityLabel?: never }
  | { decorative?: false; accessibilityLabel: string }

export type BeoflowIndicatorProps = {
  direction: IndicatorDirection
  /** Any token/color string; tints the glyph via currentColor. */
  color: string
  size?: number
} & IndicatorAccessibility

export function BeoflowIndicator(props: BeoflowIndicatorProps) {
  const { direction, color, size = indicatorSpec.sizes.small.dimension } = props
  const decorative = props.decorative === true
  const accessibilityLabel = decorative ? undefined : props.accessibilityLabel

  return (
    <Box
      component="svg"
      viewBox="0 0 72 72"
      aria-hidden={decorative ? 'true' : undefined}
      aria-label={accessibilityLabel}
      role={accessibilityLabel ? 'img' : undefined}
      focusable="false"
      data-beoflow-indicator={direction}
      sx={{ width: size, height: size, fill: color, flexShrink: 0 }}
    >
      <path d={INDICATOR_PATHS[direction]} />
    </Box>
  )
}
